import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Checkbox from '../../Checkbox';
import SidebarExtensionWrapper from './SideBarExtensionWrapper';
import SideBarSection from './SideBarSection';
import { withData } from "../../../DataController";
import VersionOption from '../Versions/VersionOption';
import GroupedVersions from '../Versions/GroupedVersions'
import FlexWrapper from '../../../layouts/FlexWrapper';
import { canUseCompare, comparePath, compareTickerKey } from '../../../layouts/ComparePage/versionApi';
import { Columns, Lock, Send } from 'react-feather';
import FilterIcon from '../../Icons/FilterIcon';
import { useState } from 'react';
import ShowMoreButton from '../../Shared/ShowMoreButton';
import { useStorage } from '../../../Utils/Utils'
import { format, parseISO, isWithinInterval, subHours, isSameHour, isBefore } from 'date-fns';

const VersionFilters = ({ filterPublished, filterLocked, onPublishedClick, onLockedClick }) => {
	return (
		<FlexWrapper align="center" gap="m">
			<FilterIcon colour="positive" onClick={onPublishedClick} toolTip="Filter by published" active={filterPublished}>
				<Send size={16} className="colour-text-positive-dark" />
			</FilterIcon>
			<FilterIcon colour="primary" onClick={onLockedClick} toolTip="Filter by locked" active={filterLocked}>
				<Lock size={16} className="colour-text-accent-2" />
			</FilterIcon>
		</FlexWrapper>
	);
}

const VersionsSidebarExtension = ({ slideIn, slideOut, template, ticker, data }) => {
	const [filterPublished, setFilterPublished] = useState(false);
	const [filterLocked, setFilterLocked] = useState(false);
	const [showMore, setShowMore] = useState(false);

	const [versions, setVersions] = useState([]);
	const [filteredVersions, setFilteredVersions] = useState([]);
	const [versionsToLoad, setVersionsToLoad] = useState(5);
	const [lastSaved, setLastSaved] = useState(null);
	const [lastSavedSize, setLastSavedSize] = useState(null);
	const [currentUser, setCurrentUser] = useState({});
	// Compare entry points are EDIT-only (RES-561 follow-up item 1): external
	// managers (EXTERNAL_EDITOR without EDIT) keep History but get no compare
	// controls, matching the narrowed /compare route guard (policy lives in
	// versionApi.canUseCompare).
	const [canCompare, setCanCompare] = useState(false);
	const storage = useStorage();
	const history = useHistory();

	// Compare Documents entry points (RES-561). Without a version the page opens on
	// its defaults (current vs latest published); a specific version compares that
	// save against the current draft — the current draft is the newer side so it
	// takes the LEFT pane (follow-up item 3). Published saves link via their
	// purge-proof pub: ref rather than the ver: row.
	const openCompare = (version) => {
		const tickerKey = compareTickerKey(data);
		if (!tickerKey || !data.templateName)
			return;
		const base = comparePath(tickerKey, data.templateName);
		if (!version) {
			history.push(base);
			return;
		}
		const ref = version.publishedDocument > 0 ? 'pub:' + version.publishedDocument : 'ver:' + version.id;
		history.push(base + '?left=current&right=' + ref);
	};

	const currentVersionsToLoad = useRef();
	// remember the latest number of versions to load
	useEffect(() => {
		currentVersionsToLoad.current = versionsToLoad;
	}, [versionsToLoad]);

	const filterByLockedAndPublished = () => {
		let filteredItems = versions.filter((version) => {
			if (filterLocked) {
				if (version.locked === false) {
					return false;
				}
			}
			if (filterPublished) {
				if (version.publishedDocument === 0) {
					return false;
				}
			}
			return true;
		})
		setFilteredVersions(groupVersionsByHour(filteredItems))
	}

	useEffect(() => {
		filterByLockedAndPublished()
	}, [versions, filterLocked, filterPublished])

	const groupVersionsByHour = (listOfIndividualVersions) => {
		let groupedListToReturn = []

		listOfIndividualVersions.forEach((nextVersion) => {

			// if groupedList is empty push first version, 
			// second in array should not convert first to an array, so push that as well
			if (groupedListToReturn.length < 2) {
				groupedListToReturn.push(nextVersion)
				return;
			}

			// get most recent grouped list element
			// if most recent is an array, get first element
			let groupedListLength = groupedListToReturn.length
			let lastInGroupedList = groupedListToReturn[groupedListLength - 1]
			let versionToCheckAgainst = Array.isArray(lastInGroupedList)
				? lastInGroupedList[0]
				: lastInGroupedList

			// check if grouped list element occurred within an hour before current version
			let latestTimeInGroupedList = parseISO(versionToCheckAgainst.savedTime)
			let nextTime = parseISO(nextVersion.savedTime)


			// if yes, add to groupedListToReturn else push to groupedListToReturn
			if (isSameHour(nextTime, latestTimeInGroupedList) && !isSameHour(nextTime, new Date())) {
				// either push to groupedListToReturn nested array or replace latest value with an array consisting of both elements
				if (Array.isArray(lastInGroupedList)) {
					groupedListToReturn[groupedListLength - 1].push(nextVersion)
				} else {
					groupedListToReturn[groupedListLength - 1] = [lastInGroupedList, nextVersion]
				}
			} else {
				groupedListToReturn.push(nextVersion)
			}
		})

		return groupedListToReturn
	}

	const dataChanged = (a, b, c) => {
		setLastSaved(new Date());
		refreshVersions();
		return new Promise((resolve) => resolve(true));

	}

	const intervalPoll = (currentVersionsToLoad) => {
		refreshVersions(currentVersionsToLoad);
	}

	const refreshVersions = (currentVersionsToLoad) => {
		let number = versionsToLoad
		if (currentVersionsToLoad && currentVersionsToLoad.current) {
			number = currentVersionsToLoad.current
		}
		data.getVersions(number).then(v => {
			if (v && v.success) {
				setVersions(v.data)
			}
		});
	}

	const saveSizeListener = (size) => {
		setLastSavedSize(size);
	}

	const loadMore = () => {
		setVersionsToLoad(v => v + 5);
	}

	useEffect(() => {
		data.addLocalListener(dataChanged);
		data.setSaveSizeListener(saveSizeListener);
		// getOrFetch: /crm/me is already cached by SessionStorage (~15 peers use
		// it), so repeat sidebar opens don't refetch and the gate resolves
		// immediately. On failure the gate stays closed (handled, not thrown) —
		// compare hidden until remount beats an unhandled rejection.
		storage.getOrFetch("/crm/me").then(me => {
			setCurrentUser(me.data);
			setCanCompare(canUseCompare(me));
		}).catch(() => {
			setCanCompare(false);
		});

		let timer1 = setInterval(() => intervalPoll(currentVersionsToLoad), 35 * 1000);

		return () => {
			// Must remove the SAME reference addLocalListener registered (the
			// DataController filters by identity) — removing refreshVersions here
			// leaked the dataChanged listener on every sidebar close, stacking a
			// state-update-on-unmounted warning and a duplicate /versions fetch
			// per save (found by the 2026-08-10 step-10 review).
			data.removeLocalListener(dataChanged);
			data.unsetSaveSizeListener();
			clearTimeout(timer1);
		}

	}, [])

	const formatDate = d => {
		return format(parseISO(d), 'dd/MM/yyyy, hh:mm a');
	}

	useEffect(() => {
		refreshVersions();
	}, [ticker, template, versionsToLoad])

	return (
		<SidebarExtensionWrapper noPadding position="right" shadow slideIn={slideIn} slideOut={slideOut} fixedHeader>
			<SideBarSection
				noContentPadding
				header="History"
				filters={
					<VersionFilters
						onLockedClick={() => setFilterLocked(!filterLocked)}
						filterLocked={filterLocked}
						onPublishedClick={() => setFilterPublished(!filterPublished)}
						filterPublished={filterPublished}
					/>
				}
			>
				{canCompare && versions && versions.length > 0 && (
					<button type="button" className="versions-compare-button" onClick={() => openCompare(null)}>
						<Columns size={14} />
						<span>Compare versions</span>
					</button>
				)}
				{lastSaved && <VersionOption id={-1} date={format(lastSaved, 'dd/MM/yyyy, hh:mm:ss a')} author={currentUser?.name} size={lastSavedSize} current />}
				{versions && (
					filteredVersions.map((v, idx) => {
						if (Array.isArray(v)) {
							return (
								<GroupedVersions
									group={v}
									versionsToLoad={versionsToLoad}
									refresh={() => refreshVersions(currentVersionsToLoad)}
									data={data}
									formatDate={formatDate}
									key={`group-${v[0].id}`}
									current={idx === 0 && lastSaved === null}
									onCompareVersion={canCompare ? openCompare : null}
								/>
							)
						} else {
							return <VersionOption key={v.id + " " + v.locked}
								id={v.id}
								data={data}
								date={formatDate(v.savedTime)}
								author={v.user}
								size={v.size}
								locked={v.locked}
								major={v.major}
								minor={v.minor}
								published={v.publishedDocument !== 0}
								versionsToLoad={versionsToLoad}
								refresh={() => refreshVersions(currentVersionsToLoad)}
								current={idx === 0 && lastSaved === null}
								compareOnClick={canCompare ? () => openCompare(v) : null}
							/>
						}
					})

				)}
				{versions.length > 1
					? <ShowMoreButton active={showMore} onClick={() => loadMore()} />
					: <FlexWrapper fullWidth justify="center" align="center" gap="m">
						<p style={{color: "#adb1bd"}}>No history to display</p>
					</FlexWrapper>
				}
			</SideBarSection>
		</SidebarExtensionWrapper>
	);
};

export default withData(VersionsSidebarExtension);